import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { TopBar } from '../components/TopBar';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { CursorSpotlight } from '../components/CursorSpotlight';
import { getDispatchBySlug, getAllDispatches } from '../lib/data';
import type { Dispatch } from '../lib/types';

marked.setOptions({ gfm: true, breaks: false });

export function DispatchPage() {
  const { slug = '' } = useParams();
  const [d, setD] = useState<Dispatch | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [siblings, setSiblings] = useState<{
    prev: Dispatch | null;
    next: Dispatch | null;
  }>({ prev: null, next: null });

  useEffect(() => {
    setLoaded(false);
    setD(null);
    window.scrollTo(0, 0);
    (async () => {
      const [row, all] = await Promise.all([
        getDispatchBySlug(slug),
        getAllDispatches(),
      ]);
      setD(row);
      setLoaded(true);
      const idx = all.findIndex((x) => x.slug === slug);
      if (idx !== -1) {
        setSiblings({
          next: idx > 0 ? all[idx - 1] : null, // newer
          prev: idx < all.length - 1 ? all[idx + 1] : null, // older
        });
      }
    })();
  }, [slug]);

  const bodyHtml = useMemo(() => {
    if (!d) return '';
    if (d.body_html && d.body_html.trim()) {
      return DOMPurify.sanitize(d.body_html, { USE_PROFILES: { html: true } });
    }
    const rendered = marked.parse(d.body_md || '', { async: false }) as string;
    return DOMPurify.sanitize(rendered, { USE_PROFILES: { html: true } });
  }, [d]);

  const signoffHtml = useMemo(
    () => (d ? DOMPurify.sanitize(d.signoff_html || '', { USE_PROFILES: { html: true } }) : ''),
    [d]
  );

  return (
    <div
      style={{
        background: 'var(--paper)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-mono)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      <CursorSpotlight />
      <TopBar />
      <Nav />
      <article
        className="section"
        style={{ padding: '56px 28px', minHeight: '60vh', maxWidth: 780, margin: '0 auto' }}
      >
        {loaded && !d && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 20 }}>
              § · Not filed
            </div>
            <h1 className="serif italic" style={{ fontSize: 48, margin: 0, marginBottom: 16 }}>
              No dispatch at this address.
            </h1>
            <Link to="/dispatches" style={{ color: 'var(--accent)' }}>
              ← all dispatches
            </Link>
          </div>
        )}
        {d && (
          <>
            <div className="eyebrow-accent" style={{ marginBottom: 8 }}>
              Quarterly Dispatch
            </div>
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                opacity: 0.6,
                marginBottom: 32,
              }}
            >
              {d.issue_label} · Filed · {d.filed_location} · ~ {d.read_minutes} min read
            </div>
            <h1
              className="serif italic"
              style={{
                fontSize: 56,
                lineHeight: 0.95,
                letterSpacing: -1.6,
                margin: 0,
                marginBottom: 36,
              }}
            >
              From the field.
            </h1>
            <div
              className="serif dispatch-body"
              style={{ fontSize: 20, lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            {signoffHtml && (
              <div
                className="serif italic"
                style={{
                  fontSize: 18,
                  marginTop: 36,
                  opacity: 0.8,
                  lineHeight: 1.5,
                }}
                dangerouslySetInnerHTML={{ __html: signoffHtml }}
              />
            )}

            <nav
              aria-label="Dispatch navigation"
              style={{
                marginTop: 56,
                borderTop: '1px solid var(--ink)',
                paddingTop: 20,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
              }}
            >
              <div>
                {siblings.prev && (
                  <Link
                    to={`/dispatches/${siblings.prev.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <div className="eyebrow" style={{ marginBottom: 4, opacity: 0.5 }}>
                      ← Earlier issue
                    </div>
                    <div className="serif" style={{ fontSize: 18, lineHeight: 1.2 }}>
                      {siblings.prev.issue_label}
                    </div>
                  </Link>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {siblings.next && (
                  <Link
                    to={`/dispatches/${siblings.next.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <div className="eyebrow" style={{ marginBottom: 4, opacity: 0.5 }}>
                      Later issue →
                    </div>
                    <div className="serif" style={{ fontSize: 18, lineHeight: 1.2 }}>
                      {siblings.next.issue_label}
                    </div>
                  </Link>
                )}
              </div>
            </nav>
            <div style={{ marginTop: 24 }}>
              <Link
                to="/dispatches"
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  textDecoration: 'none',
                  borderBottom: '1px solid var(--ink)',
                  paddingBottom: 2,
                }}
              >
                ← all dispatches
              </Link>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}
